import { useState } from "react";
import { Plus, Trash2, Save, Building2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { PageHeader, Pill } from "@/components/common";
import { SettingsTabs } from "@/components/settings/SettingsTabs";
import { useDepartments } from "@/hooks/useDepartments";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DeptRow = ({ d, onChanged }) => {
  const [name, setName] = useState(d.name);
  const call = async (fn, msg) => {
    try {
      await fn();
      toast.success(msg);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 last:border-0" data-testid={`dept-row-${d.id}`}>
      <span className="w-9 h-9 rounded-xl bg-[#EAF2F2] text-[#24474A] grid place-items-center shrink-0"><Building2 className="w-4 h-4" /></span>
      <Input value={name} onChange={(e) => setName(e.target.value)} className="max-w-xs" data-testid={`dept-name-input-${d.id}`} />
      <Pill cls="bg-slate-50 text-slate-600 border-slate-200">{d.user_count} user(s)</Pill>
      <div className="ml-auto flex gap-1">
        <Button size="icon" variant="ghost" disabled={!name.trim() || name === d.name} onClick={() => call(() => api.put(`/departments/${d.id}`, { name }), "Department renamed")} data-testid={`dept-save-btn-${d.id}`}><Save className="w-4 h-4" /></Button>
        <Button size="icon" variant="ghost" onClick={() => window.confirm(`Delete ${d.name}?`) && call(() => api.delete(`/departments/${d.id}`), "Department deleted")} data-testid={`dept-delete-btn-${d.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></Button>
      </div>
    </div>
  );
};

export default function DepartmentsSettings() {
  const { departments, reload } = useDepartments();
  const [name, setName] = useState("");

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/departments", { name });
      toast.success("Department added");
      setName("");
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div>
      <PageHeader title="Settings" subtitle="Add departments as your team grows. Renaming updates everyone in it." />
      <SettingsTabs />
      <form onSubmit={add} className="flex gap-2 mb-4 max-w-md">
        <Input placeholder="New department name" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-dept-input" />
        <Button type="submit" className="rounded-full" disabled={!name.trim()} data-testid="add-dept-btn"><Plus className="w-4 h-4 mr-1.5" /> Add</Button>
      </form>
      <div className="rounded-2xl border border-slate-200 bg-white" data-testid="departments-list">
        {departments.map((d) => <DeptRow key={`${d.id}-${d.name}`} d={d} onChanged={reload} />)}
      </div>
    </div>
  );
}
